<!-- PedidoPaso2.vue — Tabla de ítems editable + agregar artículo manual -->
<template>
  <div>
    <!-- Spinner análisis IA -->
    <div v-if="analizando" class="text-center py-5">
      <div class="spinner-border text-verde mb-3" style="width: 3rem; height: 3rem"></div>
      <p class="fw-bold text-cafe">Analizando PDF con IA...</p>
      <small class="text-muted">Puede tardar unos segundos</small>
    </div>

    <template v-else>
      <!-- Resumen coincidencias IA -->
      <div v-if="itemsDesdeIa" class="resumen-coincidencias mb-3">
        <div class="coinc-item coinc-ok">
          <i class="bi bi-check-circle-fill me-1"></i>{{ itemsConMatch }} con coincidencia
        </div>
        <div v-if="itemsSinMatch > 0" class="coinc-item coinc-warn">
          <i class="bi bi-exclamation-triangle-fill me-1"></i>
          {{ itemsSinMatch }} sin artículo — asigna manualmente o descarta
        </div>
        <div class="coinc-item coinc-info">
          <i class="bi bi-list-check me-1"></i>{{ items.length }} ítems en total
        </div>
      </div>

      <!-- ── Agregar artículo manual ─────────────────────────────────────── -->
      <!--
        overflow: visible en la card para que el dropdown no se corte.
        El border-radius se aplica solo al header y al wrapper interno.
      -->
      <div class="agregar-item-card mb-4">
        <div class="agregar-item-header">
          <i class="bi bi-plus-circle me-2"></i>Agregar artículo manualmente
        </div>

        <!-- Una sola fila: artículo | cantidad | notas | botón -->
        <div class="row g-2 align-items-end p-3">
          <div class="col-12 col-md-4" v-click-outside="cerrarDropdownLocal">
            <label class="form-label label-divisan small">Artículo</label>
            <div class="custom-select-wrapper">
              <input
                v-model="busquedaLocal"
                type="text"
                class="form-control divisan-input-modal custom-select-input"
                placeholder="Buscar por código o descripción..."
                @focus="dropdownAbierto = true"
                @input="dropdownAbierto = true"
              />
              <i
                class="bi custom-select-arrow"
                :class="dropdownAbierto ? 'bi-chevron-up' : 'bi-chevron-down'"
                @mousedown.prevent="dropdownAbierto = !dropdownAbierto"
                style="pointer-events: auto; cursor: pointer"
              ></i>
              <!-- z-index alto + position fixed-like via portal workaround -->
              <div v-if="dropdownAbierto" class="custom-dropdown dropdown-portal">
                <div
                  v-if="articulosFiltradosLocal.length === 0"
                  class="custom-dropdown-empty"
                >
                  Sin resultados
                </div>
                <div
                  v-for="a in articulosFiltradosLocal"
                  :key="a.id"
                  class="custom-dropdown-item"
                  :class="{ selected: itemNuevo.articulo_id === a.id }"
                  @mousedown.prevent="elegirArticulo(a)"
                >
                  <span class="item-codigo">{{ a.codigo }}</span>
                  <span class="item-nombre">{{ a.descripcion || a.codigo }}</span>
                  <span v-if="a.tipo === 'servicio'" class="badge-servicio ms-auto">Servicio</span>
                </div>
              </div>
            </div>
          </div>

          <div class="col-5 col-md-2">
            <label class="form-label label-divisan small">Cantidad</label>
            <input
              v-model.number="itemNuevo.cantidad"
              type="number"
              min="1"
              class="form-control divisan-input-modal"
              placeholder="0"
              @keyup.enter="handleAgregarItem"
            />
          </div>

          <div class="col-7 col-md-3">
            <label class="form-label label-divisan small">Notas</label>
            <input
              v-model="itemNuevo.notas"
              type="text"
              class="form-control divisan-input-modal"
              placeholder="Series, etc."
              @keyup.enter="handleAgregarItem"
            />
          </div>

          <div class="col-12 col-md-3">
            <button
              class="btn btn-nuevo w-100"
              @click="handleAgregarItem"
              :disabled="!itemNuevo.articulo_id || !itemNuevo.cantidad"
            >
              <i class="bi bi-plus-lg me-1"></i>Agregar
            </button>
          </div>
        </div>
      </div>

      <!-- Lista vacía -->
      <div v-if="items.length === 0" class="vacio-container">
        <i class="bi bi-inbox"></i>
        <p class="mt-2 mb-0">Aún no hay artículos en este pedido</p>
      </div>

      <!-- ── Tabla de ítems ──────────────────────────────────────────────── -->
      <template v-else>
        <small class="text-cafe fw-bold d-block mb-2">
          {{ items.length }} artículo(s) · Total piezas:
          <span class="text-verde">{{ totalPiezas.toLocaleString("es-MX") }}</span>
        </small>

        <!--
          overflow: visible para que los dropdowns inline no se corten.
          La tabla tiene su propio scroll horizontal si hace falta.
        -->
        <div class="tabla-items-wrapper">
          <table class="table table-divisan align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3" style="width: 36px"></th>
                <th style="min-width: 120px">Artículo</th>
                <th>Descripción</th>
                <th class="text-center" style="width: 100px">Cantidad</th>
                <th style="min-width: 140px">Notas / series</th>
                <th class="text-center" style="width: 48px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in items" :key="idx" :class="rowClaseItem(item)">
                <!-- Indicador: check verde (manual o IA ≥80%), amarillo (IA <80%), rojo (sin asignar) -->
                <td class="ps-3 text-center">
                  <i
                    v-if="item.articulo_id"
                    class="bi bi-check-circle-fill"
                    :style="{
                      color: item._desdeIa
                        ? item.confianza >= 80
                          ? '#2e7d32'
                          : '#b7770d'
                        : 'var(--d-green)',
                    }"
                    :title="
                      item._desdeIa
                        ? `IA — confianza ${item.confianza}%`
                        : 'Agregado manualmente'
                    "
                  >
                  </i>
                  <i
                    v-else
                    class="bi bi-exclamation-triangle-fill text-danger"
                    title="Sin artículo asignado"
                  ></i>
                </td>

                <!-- Selector inline artículo — dropdown sale hacia abajo sin cortarse -->
                <td style="position: relative; overflow: visible">
                  <div
                    class="custom-select-wrapper"
                    v-click-outside="
                      () => {
                        item._abierto = false;
                      }
                    "
                  >
                    <input
                      v-model="item._busqueda"
                      type="text"
                      class="form-control divisan-input-modal custom-select-input"
                      style="font-size: 0.82rem"
                      :placeholder="
                        item.articulo_id ? codigoArticulo(item.articulo_id) : 'Asignar...'
                      "
                      :class="{ 'input-sin-match': !item.articulo_id }"
                      @focus="item._abierto = true"
                      @input="item._abierto = true"
                    />
                    <i
                      class="bi custom-select-arrow"
                      :class="item._abierto ? 'bi-chevron-up' : 'bi-chevron-down'"
                      @mousedown.prevent="item._abierto = !item._abierto"
                      style="pointer-events: auto; cursor: pointer"
                    ></i>
                    <div v-if="item._abierto" class="custom-dropdown dropdown-portal">
                      <div
                        v-if="filtrarArticulos(item._busqueda).length === 0"
                        class="custom-dropdown-empty"
                      >
                        Sin resultados
                      </div>
                      <div
                        v-for="a in filtrarArticulos(item._busqueda)"
                        :key="a.id"
                        class="custom-dropdown-item"
                        :class="{ selected: item.articulo_id === a.id }"
                        @mousedown.prevent="asignarArticuloItem(item, a)"
                      >
                        <span class="item-codigo">{{ a.codigo }}</span>
                        <span class="item-nombre">{{ a.descripcion || a.codigo }}</span>
                        <span v-if="a.tipo === 'servicio'" class="badge-servicio ms-auto"
                          >Servicio</span
                        >
                      </div>
                    </div>
                  </div>
                </td>

                <!--
                  Descripción:
                  - IA: muestra nombre_pdf (lo que decía el documento original) + código PDF si existe
                        Los "comentarios" de la IA (↳) aparecen como chips informativos, NO en notas
                  - Manual: muestra la descripción del catálogo
                -->
                <td>
                  <template v-if="item._desdeIa">
                    <span class="nombre-pdf d-block">{{ item.nombre_pdf }}</span>
                    <small v-if="item.codigo_pdf" class="text-muted font-monospace">{{
                      item.codigo_pdf
                    }}</small>
                    <!-- Comentarios de baja confianza como chips, no como ↳ en notas -->
                    <div v-if="item.comentarios?.length" class="comentarios-ia mt-1">
                      <span
                        v-for="(c, ci) in item.comentarios"
                        :key="ci"
                        class="chip-comentario"
                      >
                        {{ c }}
                      </span>
                    </div>
                  </template>
                  <span v-else class="text-cafe small">{{
                    descripcionArticulo(item.articulo_id)
                  }}</span>
                  <span v-if="esClaveServicio(item.articulo_id)" class="badge-servicio ms-1"
                    >Servicio</span
                  >
                </td>

                <td class="text-center">
                  <input
                    v-model.number="item.cantidad"
                    type="number"
                    min="1"
                    class="form-control divisan-input-modal text-center mx-auto"
                    style="width: 95px"
                  />
                </td>

                <!-- Notas: campo libre, sin los ↳ de IA (esos van en Descripción) -->
                <td>
                  <input
                    v-model="item.notas"
                    type="text"
                    class="form-control divisan-input-modal"
                    placeholder="—"
                    style="font-size: 0.82rem"
                  />
                </td>

                <td class="text-center">
                  <button class="btn-circular" @click="quitarItem(idx)" title="Quitar">
                    <i class="bi bi-trash text-danger"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { usePedidoBadges } from "./composables/usePedidoBadges.js";
import { esClaveServicio } from "@/utils/api_backend";

const props = defineProps({
  items: { type: Array, required: true },
  itemNuevo: { type: Object, required: true },
  catArticulos: { type: Array, default: () => [] },
  analizando: { type: Boolean, default: false },
  itemsDesdeIa: { type: Boolean, default: false },
  itemsConMatch: { type: Number, default: 0 },
  itemsSinMatch: { type: Number, default: 0 },
  totalPiezas: { type: Number, default: 0 },
  codigoArticulo: { type: Function, required: true },
  descripcionArticulo: { type: Function, required: true },
  filtrarArticulos: { type: Function, required: true },
  asignarArticuloItem: { type: Function, required: true },
  agregarItem: { type: Function, required: true },
  quitarItem: { type: Function, required: true },
});

const { rowClaseItem } = usePedidoBadges();

// Estado LOCAL del dropdown "agregar artículo"
const busquedaLocal = ref("");
const dropdownAbierto = ref(false);

const articulosFiltradosLocal = computed(() =>
  props.filtrarArticulos(busquedaLocal.value)
);

function elegirArticulo(a) {
  props.itemNuevo.articulo_id = a.id;
  busquedaLocal.value = `${a.codigo} — ${a.descripcion || a.codigo}`;
  dropdownAbierto.value = false;
}
function cerrarDropdownLocal() {
  dropdownAbierto.value = false;
}

// Wrapper local: llama al composable y luego limpia los campos visuales de este componente
function handleAgregarItem() {
  props.agregarItem(); // lógica del composable (valida, pushea, resetea itemNuevo)
  busquedaLocal.value = ""; // limpia el texto visible del buscador
  dropdownAbierto.value = false; // cierra el dropdown por si quedó abierto
}

const vClickOutside = {
  mounted(el, b) {
    el._co = (e) => {
      if (!el.contains(e.target)) b.value(e);
    };
    document.addEventListener("click", el._co);
  },
  unmounted(el) {
    document.removeEventListener("click", el._co);
  },
};
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}
.label-divisan {
  color: var(--d-brown);
  font-weight: 700;
}

/* ── Card agregar ítem ──────────────────────────────────────
   overflow: visible es clave — sin esto el dropdown se corta  */
.agregar-item-card {
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.3);
  border-radius: 12px;
  overflow: visible; /* ← FIX: era hidden */
}
.agregar-item-header {
  background: var(--d-green);
  color: var(--d-cream);
  padding: 0.5rem 1rem;
  font-weight: 800;
  font-size: 0.9rem;
  border-radius: 11px 11px 0 0; /* solo esquinas superiores */
}

/* ── Tabla de ítems ─────────────────────────────────────────
   overflow: visible para que los dropdowns inline salgan    */
.tabla-items-wrapper {
  border-radius: 12px;
  border: 1px solid rgba(96, 108, 56, 0.15);
  overflow: visible; /* ← FIX: era hidden dentro de divisan-table-container */
  background: var(--d-cream);
}
.table-divisan {
  width: 100%;
  border-collapse: collapse;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 3px solid var(--d-brown);
  padding: 10px 12px;
  font-weight: 800;
  border-top: none;
  position: sticky;
  top: 0;
  z-index: 2;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 8px 10px;
}
.table-divisan tbody tr:last-child td {
  border-bottom: none;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.04);
}
.row-sin-match td {
  background-color: rgba(220, 53, 69, 0.05) !important;
}
.row-match-bajo td {
  background-color: rgba(255, 193, 7, 0.06) !important;
}

/* ── Custom select ──────────────────────────────────────────*/
.custom-select-wrapper {
  position: relative;
}
.custom-select-input {
  padding-right: 2.2rem !important;
}
.custom-select-arrow {
  position: absolute;
  right: 9px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--d-green);
  font-size: 0.8rem;
}

/* dropdown-portal: z-index muy alto para salir de cualquier stacking context */
.custom-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 9999;
  background: white;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(40, 54, 24, 0.18);
  max-height: 185px;
  overflow-y: auto;
}
.custom-dropdown-item {
  padding: 7px 12px;
  cursor: pointer;
  display: flex;
  gap: 8px;
  align-items: center;
  border-bottom: 1px solid rgba(96, 108, 56, 0.08);
  transition: background 0.12s;
}
.custom-dropdown-item:last-child {
  border-bottom: none;
}
.custom-dropdown-item:hover,
.custom-dropdown-item.selected {
  background: rgba(96, 108, 56, 0.12);
}
.custom-dropdown-empty {
  padding: 10px 12px;
  color: rgba(64, 34, 8, 0.4);
  font-size: 0.85rem;
  text-align: center;
}
.item-codigo {
  font-family: var(--d-font-mono);
  font-size: 0.8rem;
  font-weight: 700;
  background: var(--d-green-dark);
  color: var(--d-cream);
  border-radius: 4px;
  padding: 1px 6px;
  flex-shrink: 0;
}
.item-nombre {
  font-size: 0.85rem;
  color: var(--d-brown);
}
/* Envío / impresiones extra: se cobran en el pedido pero no son piezas. */
.badge-servicio {
  background: rgba(21, 101, 192, 0.12);
  color: #1565c0;
  border-radius: 20px;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 8px;
  white-space: nowrap;
}

/* ── Descripción columna ───────────────────────────────────*/
.nombre-pdf {
  font-weight: 700;
  color: var(--d-green-dark);
  font-size: 0.85rem;
}

/* Chips para comentarios IA — más discretos que ↳ en texto */
.comentarios-ia {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.chip-comentario {
  font-size: 0.7rem;
  padding: 1px 7px;
  background: #fff8e1;
  color: #b7770d;
  border: 1px solid #ffe082;
  border-radius: 20px;
  font-weight: 600;
}

/* ── Inputs ────────────────────────────────────────────────*/
.divisan-input-modal {
  border: 1px solid var(--d-green);
  border-radius: 8px;
  color: var(--d-brown);
  background: white;
}
.divisan-input-modal:focus {
  border-color: var(--d-green-dark);
  box-shadow: 0 0 0 0.2rem rgba(40, 54, 24, 0.15);
  outline: none;
}
.input-sin-match {
  border-color: #dc3545 !important;
  background-color: #fff5f5 !important;
}

/* ── Miscelánea ────────────────────────────────────────────*/
.resumen-coincidencias {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.coinc-item {
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 800;
  display: flex;
  align-items: center;
}
.coinc-ok {
  background: #e8f5e9;
  color: #2e7d32;
}
.coinc-warn {
  background: #fdecea;
  color: #c0392b;
}
.coinc-info {
  background: #e3f2fd;
  color: #1565c0;
}

.vacio-container {
  text-align: center;
  padding: 2rem;
  color: var(--d-brown);
  opacity: 0.45;
  font-weight: 700;
}
.vacio-container i {
  font-size: 2.2rem;
  display: block;
}

.btn-nuevo {
  background-color: var(--d-brown);
  color: var(--d-cream);
  border: none;
  font-weight: 600;
  border-radius: 8px;
}
.btn-nuevo:hover:not(:disabled) {
  background-color: var(--d-green-dark);
  color: var(--d-cream);
}
.btn-nuevo:disabled {
  opacity: 0.5;
}

.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 33px;
  height: 33px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;
}
.btn-circular:hover {
  background: var(--d-green);
  color: var(--d-cream);
}

.table-divisan input[type="number"]::-webkit-inner-spin-button,
.table-divisan input[type="number"]::-webkit-outer-spin-button {
  opacity: 1;
  margin-right: 4px; /* ← mueve las flechas ligeramente a la derecha */
}

/* Evita que el número se corte contra el borde derecho */
.table-divisan input[type="number"] {
  padding-right: 6px;
}
</style>
